import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet';
import { message } from 'antd';
import _ from 'lodash';

import { Typography } from 'ui-kit';
import CreateStoresButtonConfirmModal from 'components/Partners/CreateStoresButtonConfirmModal';
import LoadingModal from 'components/LoadingModal';
import OverviewLayout from 'components/OverviewLayout/OverviewLayout';
import ImportStoresTable from 'components/Partners/ImportStoresTable/ImportStoresTable';
import ErrorMessage from 'components/ErrorMessage';
import InfoMessage from 'components/InfoMessage/InfoMessage';

import {
  getIntegrationApi,
  usePollingImportStores,
  validateImportProperties,
} from 'components/Partners/utils';
import { useHistory, useParams } from 'react-router-dom';
import { getStoreList } from 'api/owners';

import './styles.scss';
import { yupResolver } from '@hookform/resolvers/yup';
import { useStoresToSync } from 'api/store_sdk';
import { IntegrationType } from 'api/types/partners';
import { ImportedStore } from 'api/types/store';
import { EImportStoreActionType } from 'components/Partners/constants';
import { useSignedUser } from 'api/auth';

const IntegrationPage = () => {
  const { company } = useParams<{ company: IntegrationType }>();

  const { data: user } = useSignedUser();
  const [isCreatingStores, setIsCreatingStores] = useState(false);
  const [massStoreRequestId, setMassStoreRequestId] = useState<number | null>(null);

  const api = useMemo(() => getIntegrationApi({ company }), [company]);

  const { data, isLoading, isError, error } = api.usePropertyList();

  const { data: storesToSync } = useStoresToSync({ platform: company });

  const history = useHistory();

  const { data: storeList, invalidate: updateStoreList } = getStoreList(user?.owner_id);

  const { setValue, handleSubmit, ...methods } = useForm({
    mode: 'all',
    defaultValues: { listings: [] },
    shouldFocusError: true,
    resolver: yupResolver(validateImportProperties),
  });

  // Set form values when data is fetched
  useEffect(() => {
    if (_.isEmpty(error) && !error) {
      setValue(
        'listings',
        data?.map((listing: ImportedStore) => ({
          ...listing,
          action: listing.existingImportSource
            ? EImportStoreActionType.SYNC
            : EImportStoreActionType.CREATE,
          storeId: listing.existingImportSource ? listing.existingImportSource.storeId : undefined,
        })),
        { shouldDirty: true },
      );
    } else {
      setValue('listings', []);
    }
  }, [data, error]);

  const { isTaskReady } = usePollingImportStores(massStoreRequestId);

  useEffect(() => {
    if (isTaskReady) {
      handleFinish();
    }
  }, [isTaskReady, updateStoreList, company]);

  const handleFinish = useCallback(() => {
    updateStoreList();
    if (storeList.stores.length > 0) {
      setIsCreatingStores(false);
      message.info('Your stores have been imported successfully!');
      history.push(`/overview/stores/?intro=${company}`);
    }
  }, [storeList, updateStoreList, company]);

  useEffect(() => {
    if (!isLoading && isError) {
      if (!error?.info) {
        message.error(
          <ErrorMessage
            message={
              error ||
              `Something went wrong! Please check if you connected TheHostCo in your ${_.capitalize(
                company,
              )} Marketplace or`
            }
            action={() => window.open('https://www.thehost.co/contact', '_blank')}
            actionLabel="contact support"
          />,
          10,
        );
      }
    }
  }, [isLoading, company, isError, error]);

  if (isCreatingStores) {
    return (
      <LoadingModal
        title="Creating Stores"
        topCopy="Thank you for your patience while we get your stores."
        bottomCopy="Please don’t leave or refresh this page until the process is done."
        isVisible={isCreatingStores}
      />
    );
  }

  return (
    <FormProvider setValue={setValue} handleSubmit={handleSubmit} {...methods}>
      <OverviewLayout>
        <Helmet>
          <title>Partnered with {_.capitalize(company)} | The Host Co</title>
        </Helmet>
        <div className="Integration">
          {isLoading ? (
            <LoadingModal
              title="Importing properties..."
              topCopy="Thank you for your patience while we get your properties."
              bottomCopy="Please don't leave or refresh this page until the process is done."
              isVisible={isLoading}
            />
          ) : (
            <div className="Integration_content">
              {isError && error?.info ? (
                <div className="Integration_info-message-container" style={{ padding: '20px 0' }}>
                  <InfoMessage
                    messageBeforeLink={error.info.messageBeforeLink}
                    messageAfterLink={error.info.messageAfterLink}
                    action={() => window.open(`${error.info.actionUrl}`, '_blank')}
                    actionUrl={error.info.actionLabel}
                  />
                </div>
              ) : (
                <>
                  <div className="Integration_header">
                    <div className="Integration_header-text">
                      <Typography variant="h4" className="Integration_header-title">
                        Select Properties
                      </Typography>
                      <Typography variant="body2">
                        Select which properties you&apos;d like to create stores from.
                      </Typography>
                      <Typography variant="body2">
                        (You can do this again later to import even more properties)
                      </Typography>
                    </div>
                    <div className="Integration_header-button">
                      <CreateStoresButtonConfirmModal
                        ownerId={user?.owner_id}
                        setIsCreatingStores={setIsCreatingStores}
                        setMassStoreRequestId={setMassStoreRequestId}
                        onFinishSync={handleFinish}
                      />
                    </div>
                  </div>
                  <ImportStoresTable listings={!isError ? data : []} storesToSync={storesToSync} />
                </>
              )}
            </div>
          )}
        </div>
      </OverviewLayout>
    </FormProvider>
  );
};

export default IntegrationPage;
